/** Copyright (c) 2025 DAIA Tech Pvt Ltd. All rights reserved.
 *
 * This software is confidential and proprietary information of DAIA Tech Pvt Ltd
 * and its licensors. You shall not disclose such Confidential Information and shall
 * use it only in accordance with the terms of the license agreement.
 *
 * Unauthorized reproduction, modification, distribution, or disclosure of this
 * software is strictly prohibited.
 */

import { createApi, fetchBaseQuery, type FetchBaseQueryArgs } from '@reduxjs/toolkit/query/react';
import type {
  GenerateOTPArgs,
  GenerateOTPResponse,
  RenewTokenArgs,
  RenewTokenResponse,
  ResendOTPArgs,
  ResendOTPResponse,
  UpdateWebProfileArgs,
  UpdateWebProfileResponse,
  VerifyOTPArgs,
  VerifyOTPResponse,
} from './Types';
import { upsertWorker, upsertWorkers } from '../../db/Worker';
import { setKaryaWorkerIdToken } from '../PrepareHeader';

const BASE_AUTHSERVER_URL = import.meta.env.VITE_KARYA_AUTHSERVER;
if (!BASE_AUTHSERVER_URL) {
  throw new Error('Missing required env: VITE_KARYA_AUTHSERVER');
}

// Define a service using a base URL and expected endpoints
export let authApi: ReturnType<typeof buildAuthApi>;

/**
 * Sets up the Auth API with the given fetch function.
 * @param fetchFn - Optional custom fetch function to use for requests.
 * @returns The configured Auth API.
 */
export function buildAuthApi(fetchFn?: FetchBaseQueryArgs['fetchFn']) {
  return createApi({
    reducerPath: 'authserver',
    baseQuery: fetchBaseQuery({
      fetchFn,
      baseUrl: BASE_AUTHSERVER_URL,
      prepareHeaders: async (headers, api) => {
        // Skip adding token for OTP related endpoints
        if (['generateOTP', 'resendOTP', 'verifyOTP'].includes(api.endpoint)) {
          return headers;
        }

        // For other endpoints, set the Karya Worker ID Token
        return await setKaryaWorkerIdToken(headers, api);
      },
    }),

    endpoints: (build) => ({
      // Generate OTP for a given phone number
      generateOTP: build.mutation<GenerateOTPResponse, GenerateOTPArgs>({
        query: ({ phone_number }) => ({
          url: `/api_auth/v5/otp/generate`,
          headers: {
            phone_number,
          },
          method: 'POST',
        }),
      }),

      // Resend OTP for a given phone number and OTP ID
      resendOTP: build.mutation<ResendOTPResponse, ResendOTPArgs>({
        query: ({ phone_number, otp_id }) => ({
          url: `/api_auth/v5/otp/resend`,
          headers: {
            phone_number,
            otp_id,
          },
          method: 'PUT',
        }),
      }),

      // Verify OTP and upsert workers into the database
      verifyOTP: build.mutation<VerifyOTPResponse, VerifyOTPArgs>({
        query: ({ phone_number, otp_id, otp }) => ({
          url: `/api_auth/v5/otp/verify`,
          headers: {
            phone_number,
            otp_id,
            otp,
          },
          method: 'PUT',
          responseHandler: async (response) => {
            if (!response.ok) {
              return response.json();
            }

            const workers: VerifyOTPResponse = await response.json();
            const upsertWorkersResult = await upsertWorkers(workers);
            if (upsertWorkersResult.isErr()) {
              console.error('Failed to upsert workers after OTP verification:', upsertWorkersResult.error);
            }

            // Set the first worker as the active worker in localStorage
            localStorage.setItem('worker_id', workers[0].id);
            return workers;
          },
        }),
      }),

      // Renew token and upsert the worker into the database
      renewToken: build.mutation<RenewTokenResponse, RenewTokenArgs>({
        query: () => ({
          url: `/api_auth/v5/token/renew`,
          method: 'GET',
          responseHandler: async (response) => {
            if (!response.ok) {
              return response.json();
            }
            const worker: RenewTokenResponse = await response.json();
            const upsertWorkerResult = await upsertWorker(worker);
            if (upsertWorkerResult.isErr()) {
              console.error('Failed to upsert workers after token renewal:', upsertWorkerResult.error);
            }

            return worker;
          },
        }),
      }),

      // Update worker profile (web client specific)
      updateWebProfile: build.mutation<UpdateWebProfileResponse, UpdateWebProfileArgs>({
        query: ({ profile }) => ({
          url: `/api_auth/v5/web/profile`,
          method: 'PUT',
          body: profile,
          responseHandler: async (response) => {
            if (!response.ok) {
              return response.json();
            }
            const worker: UpdateWebProfileResponse = await response.json();
            const upsertWorkerResult = await upsertWorker(worker);
            if (upsertWorkerResult.isErr()) {
              console.error('Failed to upsert worker after profile update:', upsertWorkerResult.error);
            }
            return worker;
          },
        }),
      }),
    }),
  });
}

export function setAuthApi(value: ReturnType<typeof buildAuthApi>) {
  authApi = value;
}

setAuthApi(buildAuthApi());
